// Personen op het capaciteitsscherm: sleutel, weergavenaam en vrije ruimte.
//
// Waarom dit bestaat: het scherm hing alles op aan de VOORNAAM. Bij vijf mensen die
// "Bram" heetten (één medewerker, vier freelancers) overschreef de laatste de rest:
// vijf rijen met exact dezelfde cijfers — allemaal die van Bram Zweers — en omdat ook
// het type per voornaam werd opgezocht, glipten de freelancer-Brams door het
// freelancerfilter. Alles loopt nu via het record-id; de naam is alleen nog label.

import { afwezigeDagen, afwezigAlsTaken } from '../PlannerTodo/rooster';

// Tussenvoegsels tellen niet als achternaam-initiaal: "Bram van Kaauwen" → "Bram K."
const TUSSENVOEGSELS = new Set(['van', 'de', 'der', 'den', 'het', 'ten', 'ter', 'te', 'in', "'t", 'op', 'la', 'le', 'du', 'da', 'di']);

const schoon = (s) => String(s || '').replace(/\s+/g, ' ').trim();

/** Voornaam zoals we hem tonen (valt terug op het eerste woord van de volledige naam). */
export const voornaam = (p) => schoon(p?.firstName) || schoon(p?.name).split(' ')[0] || 'Onbekend';

/** Initiaal van de achternaam, tussenvoegsels overgeslagen. '' als er geen achternaam is. */
export function achternaamInitiaal(p) {
  const vol = schoon(p?.name).replace(/\(.*?\)/g, '').trim();
  const voor = voornaam(p);
  const rest = vol.toLowerCase().startsWith(voor.toLowerCase()) ? vol.slice(voor.length) : vol.split(' ').slice(1).join(' ');
  const woorden = schoon(rest).split(' ').filter(Boolean);
  const kern = woorden.find(w => !TUSSENVOEGSELS.has(w.toLowerCase())) || woorden[0] || '';
  return kern ? kern[0].toUpperCase() : '';
}

const eersteFunctie = (p) => {
  const f = Array.isArray(p?.function) ? p.function.join(', ') : String(p?.function || '');
  return schoon(f.split(',')[0]);
};

/**
 * Hoort iemand van dit Personen-type standaard in Capaciteit (rijen én de keuzelijst
 * "Personen naast elkaar")? Freelancers/Bedrijf en stagiairs alleen met hun vinkje aan.
 * Type kan uit Airtable als lijst (multipleSelects) of tekst komen.
 */
export function magInKeuzelijst(type, { showFreelancers = false, showStagiairs = false } = {}) {
  const t = Array.isArray(type) ? type.join(', ') : String(type || '');
  if (!showStagiairs && t.includes('Stagiair')) return false;
  if (!showFreelancers && /Freelancer|Bedrijf/.test(t)) return false;
  return true;
}

/**
 * Unieke weergavenaam per persoon-id. Voornaam waar dat kan; bij een botsing
 * voornaam + achternaam-initiaal ("Bram Z."); botst dat nog, de volledige naam;
 * en zijn zelfs die gelijk (dubbel record), dan de functie erachter.
 * @returns {Object<string,string>} id → label
 */
export function persoonLabels(persons) {
  const lijst = (persons || []).filter(p => p && p.id);
  const groepeer = (fn, ids) => {
    const m = new Map();
    ids.forEach(id => { const k = fn(id).toLowerCase(); m.set(k, [...(m.get(k) || []), id]); });
    return m;
  };
  const perId = new Map(lijst.map(p => [p.id, p]));
  const labels = {};
  const trappen = [
    (id) => voornaam(perId.get(id)),
    (id) => { const p = perId.get(id); const i = achternaamInitiaal(p); return i ? `${voornaam(p)} ${i}.` : voornaam(p); },
    (id) => schoon(perId.get(id).name) || voornaam(perId.get(id)),
    (id) => { const p = perId.get(id); const f = eersteFunctie(p); return `${schoon(p.name) || voornaam(p)}${f ? ` · ${f}` : ''}`; },
  ];
  let open = lijst.map(p => p.id);
  trappen.forEach((trap, i) => {
    const laatste = i === trappen.length - 1;
    const volgende = [];
    groepeer(trap, open).forEach((ids) => {
      if (ids.length === 1 || laatste) ids.forEach(id => { labels[id] = trap(id); });
      else volgende.push(...ids);
    });
    open = volgende;
  });
  // Nog steeds gelijk (zelfde naam én functie): nummeren, zodat de rijen te onderscheiden zijn.
  const gezien = {};
  Object.keys(labels).sort().forEach(id => {
    const k = labels[id].toLowerCase();
    gezien[k] = (gezien[k] || 0) + 1;
    if (gezien[k] > 1) labels[id] = `${labels[id]} (${gezien[k]})`;
  });
  return labels;
}

/**
 * E-mailadressen die bij meer dan één persoon horen (bijv. freelance@fullframe.nl).
 * Zo'n adres is een gedeelde postbus, geen eigen agenda: de afspraken daarin horen
 * bij niemand in het bijzonder en mogen dus niet bij iedere freelancer optellen.
 */
export function gedeeldeEmails(persons) {
  const tel = {};
  (persons || []).forEach(p => {
    const e = String(p?.email || '').trim().toLowerCase();
    if (e) tel[e] = (tel[e] || 0) + 1;
  });
  return new Set(Object.keys(tel).filter(e => tel[e] > 1));
}

/**
 * Vrije uren over de getoonde dagen: per werkdag wat er nog tot een volle dag past.
 * Vrije dagen en draaidagen leveren niets op — daar kan niets bij.
 */
export function vrijeUren(urenPerDag, dayISOs, hoursPerDay = 8, dagStaat = {}) {
  return (dayISOs || []).reduce((som, iso) => {
    if (dagStaat?.[iso]) return som;
    return som + Math.max(0, hoursPerDay - (Number(urenPerDag?.[iso]) || 0));
  }, 0);
}

/**
 * Zet ids in volgorde. 'naam' = alfabetisch op label; 'ruimte' = meeste vrije uren
 * eerst (gelijk → op naam), zodat wie werk kan hebben bovenaan staat.
 */
export function sorteerPersonen(ids, modus, labels = {}, vrij = {}) {
  const opNaam = (a, b) => String(labels[a] || a).localeCompare(String(labels[b] || b), 'nl');
  const kopie = [...(ids || [])];
  if (modus === 'ruimte') return kopie.sort((a, b) => ((vrij[b] || 0) - (vrij[a] || 0)) || opNaam(a, b));
  return kopie.sort(opNaam);
}

/**
 * Rijvolgorde die niet springt (wiki Capaciteit 27 sep: rijen sprongen toen de Outlook-agenda
 * binnenkwam, ook met een uitgeklapte persoon). Zolang niet alles binnen is (`klaar` = taken
 * én agenda, of de agenda definitief mislukt) de rustige `terugval`-volgorde (alfabetisch);
 * daarna één keer sorteren (`bereken`) en die volgorde vasthouden zolang de `sleutel`
 * (periode + sortering + personenkeuze) gelijk blijft. Latere stille verversingen verschuiven
 * niets meer; nieuwe ids sluiten achteraan aan (in berekende volgorde), verdwenen vallen weg.
 * @param {{ ids: string[], sleutel: string, klaar: boolean, bereken: () => string[],
 *   terugval: () => string[], vorige: ({ sleutel: string, volgorde: string[] }|null) }} opts
 * @returns {{ volgorde: string[], stand: ({ sleutel: string, volgorde: string[] }|null) }}
 *   `stand` bewaart de aanroeper (ref) en geeft hem de volgende keer als `vorige` mee.
 */
export function stabieleVolgorde({ ids, sleutel, klaar, bereken, terugval, vorige = null }) {
  if (!klaar) return { volgorde: terugval(), stand: vorige?.sleutel === sleutel ? vorige : null };
  if (!vorige || vorige.sleutel !== sleutel) {
    const volgorde = bereken();
    return { volgorde, stand: { sleutel, volgorde } };
  }
  const nu = new Set(ids || []);
  const gehouden = vorige.volgorde.filter((id) => nu.has(id));
  const bekend = new Set(gehouden);
  const nieuw = bereken().filter((id) => !bekend.has(id));
  const volgorde = [...gehouden, ...nieuw];
  return { volgorde, stand: { sleutel, volgorde } };
}

/**
 * Zet wie overboekt is (>100%) bovenaan, meest overboekt eerst; de rest houdt zijn
 * volgorde. Anders zakt een overboekte persoon bij 'Ruimte' naar onderen, buiten beeld.
 * @param {string[]} ids   al gesorteerde persoon-ids
 * @param {Object<string, number|null>} pct  bezettingspercentage per id
 */
export function overboektenBovenaan(ids, pct = {}, dagOver = {}) {
  const lijst = ids || [];
  // Ook wie gemiddeld ruimte heeft maar op één dag te veel staat (10u op maandag,
  // rest leeg = 25%) hoort bovenaan: het gemiddelde verstopt die overboeking.
  const isOver = (id) => isOverSignaal(pct[id]) || Boolean(dagOver?.[id]);
  const over = lijst.filter(isOver);
  if (!over.length) return lijst;
  const rest = lijst.filter((id) => !isOver(id));
  return [...over.sort((a, b) => Number(pct[b]) - Number(pct[a])), ...rest];
}

/**
 * Link naar Planning → Nog in te plannen met persoon en dag voorgekozen: daar staat de
 * te-plannen-bak (het Rooster heeft hem sinds 25 sep niet meer). Het planbord opent op
 * de week van die dag en plant bij snel toewijzen vanaf die dag.
 */
export const inplanLink = (persoonId, iso) =>
  `/planning/inplannen?${new URLSearchParams({ persoon: persoonId, datum: iso }).toString()}`;

// ─── Afwezigheid uit de agenda ───────────────────────────────────────────────
// Een vakantie die als gewone Outlook-afspraak is ingevoerd ("Vakantie" 08:30–17:00)
// kwam hier binnen als 8,5 uur meeting: iemand stond op zijn vrije dag als vol of
// overboekt. De Planner To-Do had dat al opgelost; we gebruiken dezelfde regels
// (rooster.js), zodat beide schermen dezelfde dag hetzelfde noemen.

/**
 * Past agenda-afwezigheid toe op de taken en agenda-uren van één persoon.
 * Afwezige dagen worden een 'Vrije dag' (dus geen beschikbare uren) en hun
 * agenda-uren verdwijnen uit de meetings, anders telde dezelfde dag dubbel.
 * @returns {{ tasks, cal, afwezig: string[] }}
 */
export function pasAfwezigheidToe(tasks, cal, afwezigMap, events, hoursPerDay = 8) {
  const afwezig = afwezigeDagen(afwezigMap, events, hoursPerDay);
  if (!afwezig.length) return { tasks: tasks || [], cal: cal || {}, afwezig };
  const weg = new Set(afwezig);
  const zonder = (m) => Object.fromEntries(Object.entries(m || {}).filter(([d]) => !weg.has(d)));
  const taken = [];
  (tasks || []).forEach((t) => {
    if (!t?.hoursByDay || t.type !== 'Afspraak') { taken.push(t); return; }
    const hoursByDay = zonder(t.hoursByDay);
    const dagen = Object.keys(hoursByDay).sort();
    if (!dagen.length) return;
    taken.push({
      ...t, hoursByDay,
      hours: Object.values(hoursByDay).reduce((s, h) => s + (Number(h) || 0), 0),
      startDate: dagen[0], endDate: dagen[dagen.length - 1],
    });
  });
  return { tasks: [...taken, ...afwezigAlsTaken(afwezig)], cal: zonder(cal), afwezig };
}

// ─── Rustige cel-signalen ────────────────────────────────────────────────────

/** Is deze dag écht overboekt (meer werk dan uren, of werk bovenop een draaidag)? */
export const isOverboekt = (uren, hoursPerDay = 8, staat, extra = 0) => {
  if (staat === 'vrij') return false;
  if ((Number(extra) || 0) > 0.05) return true;
  return !staat && (Number(uren) || 0) > hoursPerDay + 0.05;
};

/** Overboekte dagen van één persoon (iso's, in volgorde). */
export function overboekteDagen(urenPerDag, dayISOs, hoursPerDay = 8, dagStaat = {}, extra = {}) {
  return (dayISOs || []).filter((iso) => isOverboekt(urenPerDag?.[iso], hoursPerDay, dagStaat?.[iso], extra?.[iso]));
}

/**
 * Hoe luid een dagcel mag zijn. Wie ruimte heeft staat stil (lichtgrijs), een volle
 * dag donker, en alleen overboekt krijgt nadruk.
 * @returns 'leeg' | 'ruimte' | 'vol' | 'over' | 'vrij' | 'draaidag' | 'draaidag_optie'
 */
export function celNiveau(uren, hoursPerDay = 8, staat, extra = 0) {
  if (isOverboekt(uren, hoursPerDay, staat, extra)) return 'over';
  if (staat) return staat;
  const u = Number(uren) || 0;
  if (u <= 0.05) return 'leeg';
  return u / hoursPerDay >= 0.8 ? 'vol' : 'ruimte';
}

// ─── Bezetting over de periode (compacte weergave + teamtotaal) ──────────────
// Per persoon één getal i.p.v. een muur van dagcijfers. Afwezige dagen tellen niet
// als capaciteit; een draaidag is capaciteit die helemaal ingepland is. Zo geldt
// altijd: capaciteit − ingepland + overboekt = vrij.

/**
 * @returns {{ capaciteit, ingepland, vrij, overboekt, pct: number|null }}
 *   pct is null als er in de periode geen beschikbare uren zijn (hele periode afwezig).
 */
export function persoonBezetting(urenPerDag, dayISOs, hoursPerDay = 8, dagStaat = {}, extra = {}) {
  const t = { capaciteit: 0, ingepland: 0, vrij: 0, overboekt: 0 };
  (dayISOs || []).forEach((iso) => {
    const staat = dagStaat?.[iso];
    if (staat === 'vrij') return;
    t.capaciteit += hoursPerDay;
    if (staat) {
      const e = Math.max(0, Number(extra?.[iso]) || 0);
      t.ingepland += hoursPerDay + e;
      t.overboekt += e;
      return;
    }
    const u = Math.max(0, Number(urenPerDag?.[iso]) || 0);
    t.ingepland += u;
    t.overboekt += Math.max(0, u - hoursPerDay);
    t.vrij += Math.max(0, hoursPerDay - u);
  });
  return { ...t, pct: t.capaciteit > 0 ? Math.round((t.ingepland / t.capaciteit) * 100) : null };
}

/** Som van persoonBezetting over een lijst mensen: { id: bezetting } → teamtotaal. */
export function teamTotaal(ids, bezettingPerId = {}) {
  const t = { capaciteit: 0, ingepland: 0, vrij: 0, overboekt: 0 };
  (ids || []).forEach((id) => {
    const b = bezettingPerId[id];
    if (!b) return;
    Object.keys(t).forEach((k) => { t[k] += Number(b[k]) || 0; });
  });
  return t;
}

/** Alleen echt méér dan vol (>100%) krijgt de signaalkleur; 81–100% blijft neutraal. */
export const isOverSignaal = (pct) => Number(pct) > 100;

/**
 * Breedtes (in % van de balk) voor één bezettingsbalk. De balk loopt tot `schaal`%
 * zodat overboeking als rood stuk voorbij de 100%-streep zichtbaar is.
 */
export function balkDelen(pct, schaal = 125) {
  const p = Math.max(0, Number(pct) || 0);
  return {
    vol: (Math.min(p, 100) / schaal) * 100,
    over: (Math.max(0, Math.min(p, schaal) - 100) / schaal) * 100,
    streep: (100 / schaal) * 100,
  };
}

/** "1476" → "1.476" (Nederlandse duizendtallen), één decimaal met komma. Voor exacte weergave. */
export const fmtUrenNl = (h) => {
  const r = Math.round((Number(h) || 0) * 10) / 10;
  return r.toLocaleString('nl-NL', { maximumFractionDigits: 1 });
};

/**
 * Uren als heel getal, Nederlandse notatie ("70", "1.476"). Voor het overzicht,
 * de teamregel en de Vrij-kolom: achter de komma zegt daar niemand iets
 * (Joris, 25 sep: "overbodige cijfers achter de komma mag weg").
 */
export const fmtUrenHeel = (h) => Math.round(Number(h) || 0).toLocaleString('nl-NL');

/**
 * Uren in de uitklap: heel, of met een half uur als "½" ("3½", "½"). Afgerond op het
 * halve uur; een klein beetje (< ¼u) wordt "½" en geen lege cel, zodat er wél
 * zichtbaar iets staat. 0 of minder → ''.
 */
export function fmtUrenHalf(h) {
  const n = Number(h) || 0;
  if (n <= 0.001) return '';
  const r = Math.max(0.5, Math.round(n * 2) / 2);
  const heel = Math.floor(r);
  const half = r - heel >= 0.5;
  if (!half) return heel.toLocaleString('nl-NL');
  return heel === 0 ? '½' : `${heel.toLocaleString('nl-NL')}½`;
}

/** Heeft iemand in de periode ergens een overboekte dag (los van het gemiddelde)? */
export const heeftDagOverboeking = (bezetting, overDagen = []) =>
  (Number(bezetting?.overboekt) || 0) > 0.05 || (overDagen || []).length > 0;

// ─── Opslag in de browser ────────────────────────────────────────────────────
// localStorage kan gooien (privémodus, geblokkeerde sitedata) of rommel bevatten.
// Het scherm mag daar nooit op stuklopen: lezen valt terug op de standaard.

/**
 * @param {string} sleutel
 * @param {*} standaard   waarde als er niets (bruikbaars) staat
 * @param {(ruw: string) => *} [lees]  zet de opgeslagen tekst om; geeft undefined → standaard
 */
export function leesOpslag(sleutel, standaard, lees = (x) => x) {
  try {
    const ruw = window.localStorage.getItem(sleutel);
    if (ruw == null) return standaard;
    const v = lees(ruw);
    return v === undefined ? standaard : v;
  } catch {
    return standaard;
  }
}

export function schrijfOpslag(sleutel, waarde) {
  try { window.localStorage.setItem(sleutel, String(waarde)); } catch { /* privémodus */ }
}

// ─── Week opbouwen uit taken + agenda ────────────────────────────────────────

export const LEGE_AGENDA = Object.freeze({ hours: {}, events: {}, afwezig: {} });

/**
 * Bouwt de schermdata van één periode uit personen, taken per persoon-id en (mogelijk
 * nog lege) agenda. Los van het ophalen, zodat de lijst al kan staan terwijl de agenda
 * nog laadt: dan komt dezelfde functie nog een keer langs mét agenda.
 */
export function bouwWeekData(persons, tasksByPersonId = {}, agenda = LEGE_AGENDA) {
  const cal = agenda || LEGE_AGENDA;
  const gedeeld = gedeeldeEmails(persons);
  const eigenEmail = (p) => { const e = (p.email || '').toLowerCase(); return e && !gedeeld.has(e) ? e : ''; };
  const labels = persoonLabels(persons);
  const assignees = [];
  const tasksByAssignee = {};
  const calByName = {};
  const agendaByName = {};
  const afwezigByName = {};
  const eventsByName = {};
  // Mailboxen die de engine niet kon lezen (403/throttle): die persoon is niet "vrij",
  // zijn agenda is onbekend. Het scherm toont dat stil in plaats van 0 uur meetings.
  const nietGelezen = {};
  Object.entries(cal.niet_gelezen || {}).forEach(([e, v]) => { nietGelezen[String(e).toLowerCase()] = v || 'niet gelezen'; });
  const agendaOnbekendByName = {};
  (persons || []).forEach((p) => {
    const id = p?.id;
    if (!id) return;
    // Airtable-Afspraak eruit (meetings komen uit de agenda), rest behouden.
    const base = (tasksByPersonId?.[id] || []).filter((t) => t.type !== 'Afspraak');
    const email = eigenEmail(p);
    const uren = (cal.hours || {})[email] || {};
    const events = (cal.events || {})[email] || [];
    const perDag = {};
    events.forEach((ev) => { (perDag[ev.date] = perDag[ev.date] || []).push(ev); });
    const dagen = Object.keys(uren).sort();
    const totaal = Object.values(uren).reduce((s, h) => s + Number(h || 0), 0);
    // De afspraken die de meeste tijd kosten als naam; de rest in de tooltip.
    const perOnderwerp = {};
    events.forEach((ev) => {
      const t = (ev.subject || 'Afspraak').trim();
      perOnderwerp[t] = (perOnderwerp[t] || 0) + Number(ev.hours || 0);
    });
    const onderwerpen = Object.entries(perOnderwerp).sort((a, b) => b[1] - a[1]).map(([t]) => t);
    const meetingNaam = onderwerpen.length === 0
      ? 'Meetings (agenda)'
      : onderwerpen.slice(0, 2).join(' \u00b7 ') + (onderwerpen.length > 2 ? ` +${onderwerpen.length - 2}` : '');
    const meetingTasks = dagen.length ? [{
      id: `cal-${id}`, name: meetingNaam, type: 'Afspraak', status: 'Afspraak',
      hours: totaal, hoursByDay: uren, agendaEvents: perDag,
      startDate: dagen[0], endDate: dagen[dagen.length - 1],
    }] : [];
    assignees.push(id);
    tasksByAssignee[id] = [...base, ...meetingTasks];
    calByName[id] = uren;
    agendaByName[id] = perDag;
    afwezigByName[id] = (cal.afwezig || {})[email] || {};
    eventsByName[id] = events;
    if (email && nietGelezen[email]) agendaOnbekendByName[id] = nietGelezen[email];
  });
  return {
    persons: persons || [], labels, assignees: sorteerPersonen(assignees, 'naam', labels),
    tasksByAssignee, calByName, agendaByName, afwezigByName, eventsByName, agendaOnbekendByName,
  };
}

/** E-mailadressen waarvan we de agenda opvragen (gedeelde postbussen niet). */
export function agendaEmails(persons) {
  const gedeeld = gedeeldeEmails(persons);
  return (persons || [])
    .map((p) => String(p?.email || '').toLowerCase())
    .filter((e) => e && !gedeeld.has(e));
}
